import React from "react";
import { ISignable, ILockable, ISoftDeletable, EntityConstants } from "../../../api/GenericTypes";
import { LucideIcon } from "../../icon/LucideIcon";
import { CSSProperties } from "remirror";

interface EntityStateIndicatorProps<Entity extends Partial<ISignable> & Partial<ILockable> & Partial<ISoftDeletable>> {
  entityConstants?: EntityConstants;
  entity?: Entity;
  hideLabels?: boolean;
  style?: CSSProperties;
}

/**
 * Displays visual indicators for the state of an entity, such as "Trashed", "Signed", or "Locked".
 * @author @CorradoSurmanowicz
 * The component renders badges based on the properties of the provided entity:
 * - Shows a "Trashed" badge if `isDeleted` is true.
 * - Shows a "Signed" badge if `isSigned` is true.
 * - Shows a "Locked" badge if `isLocked` is true.
 *
 * If none of these states are present, nothing is rendered.
 *
 * @typeParam Entity - The entity type, which may partially implement `ISignable`, `ILockable`, and `ISoftDeletable`.
 * @param entityConstants - Constants describing the entity, such as its singular name.
 * @param entity - The entity object whose state is to be indicated.
 * @param hideLabels - If true, hides the textual labels next to the icons.
 * @returns A set of badges indicating the entity's state, or nothing if the entity has no relevant state.
 */
export const EntityStateIndicator = <Entity extends Partial<ISignable> & Partial<ILockable> & Partial<ISoftDeletable>>({
  entityConstants,
  entity,
  hideLabels,
  style,
}: EntityStateIndicatorProps<Entity>) => {
  if (!entity) return <></>;
  if (!entity?.isDeleted && !entity?.isSigned && !entity?.isLocked) return <></>;
  return (
    <>
      {!!entity?.isDeleted ? (
        <label
          className="label label-soft-warning"
          title={`This ${entityConstants?.entitySingular ?? "entity"} has been moved to trashed.`}
          style={{ margin: 0, height: "min-content", ...style }}
        >
          <LucideIcon name="trash" /> {!hideLabels && "Trashed"}
        </label>
      ) : !!entity?.isSigned ? (
        <label
          className="label label-soft-primary"
          title={`This ${entityConstants?.entitySingular ?? "entity"} has been signed.`}
          style={{ margin: 0, height: "min-content", ...style }}
        >
          <LucideIcon name="signature" /> {!hideLabels && "Signed"}
        </label>
      ) : !!entity?.isLocked ? (
        <label
          className="label label-soft-info"
          title={`This ${entityConstants?.entitySingular ?? "entity"} has been locked.`}
          style={{ margin: 0, height: "min-content", ...style }}
        >
          <LucideIcon name="lock" /> {!hideLabels && "Locked"}
        </label>
      ) : (
        <></>
      )}
    </>
  );
};
